import { ApiError } from '@/api/client';
import { i18n, t } from '@/i18n';

/** The error envelope every backend failure uses. See the backend's docs/API.md. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    requestId?: string;
    details?: unknown;
  };
}

/**
 * Whatever a `catch` caught, as something with a `code` on it.
 *
 * **The single most duplicated two lines in this SPA** — five views, two composables and the games
 * store each wrote it out, and two of them had already drifted. `stores/games.ts` said "Something
 * went wrong" where the rest said "Something failed", and passed the caught value as the
 * constructor's fifth argument, which is `details`: the slot `maxFrom`, `statusFrom` and
 * `conflictsFrom` read a parsed API payload out of. A DOM exception in there is a value every one
 * of those readers would have to defend against.
 *
 * **Status 0** means the request never got an answer — a dropped connection, an aborted fetch, a
 * bug in our own handler — as opposed to any status the API could have sent.
 *
 * The original goes in `cause`, which is what `cause` is for, and is the only place a `TypeError:
 * Failed to fetch` can still be read in a devtools console after this has flattened it.
 */
export function asApiError(caught: unknown): ApiError {
  if (caught instanceof ApiError) return caught;

  return new ApiError(0, 'UNKNOWN_ERROR', 'Something failed', undefined, undefined, caught);
}

/**
 * The message for an error code, in the reader's language.
 *
 * Anything without a key falls back to the backend's own message, which is written to be
 * readable — and is **English whatever the reader chose**, because the API has no view of who
 * is asking. Every code the SPA can actually provoke therefore needs a key in
 * `src/i18n/messages/en.ts`; the fallback is for the ones nothing here can cause.
 */
export function userMessage(code: string, fallback: string): string {
  const key = `errors.${code}`;
  return i18n.global.te(key) ? t(key) : fallback;
}

/**
 * The right sentence for a `409 GAME_NOT_OPEN`.
 *
 * One code covers locked, completed and cancelled, and the API sends the real status in
 * `details.status` so a screen need not say the vague version. A cancelled game is not a locked
 * one, and telling somebody the host "stopped taking picks" when the game was called off is a
 * small lie the details make unnecessary.
 */
export function gameNotOpenMessage(status: unknown): string {
  const key = `errors.notOpen.${String(status)}`;
  return i18n.global.te(key) ? t(key) : t('errors.GAME_NOT_OPEN');
}

/** Pulls `status` out of an ApiError's `details`, which comes off the wire and is not trusted. */
export const statusFrom = (details: unknown): unknown =>
  (details as { status?: unknown } | undefined)?.status;

/** Pulls a whole-number `max` out of an ApiError's `details`, which comes off the wire untrusted. */
export const maxFrom = (details: unknown): number | undefined => {
  const max = (details as { max?: unknown } | undefined)?.max;
  return typeof max === 'number' && Number.isInteger(max) ? max : undefined;
};

/**
 * `409 LIMIT_EXCEEDED` in the host's voice.
 *
 * The mapped message above is written for the player picking their own squares — "that would put
 * *you* over" — and on the manage view there is no "you": the host is spending somebody else's
 * allowance on their behalf. The number comes from `details.max`, and is left out rather than
 * guessed when it is not there.
 */
export const hostLimitMessage = (details: unknown): string => {
  const max = maxFrom(details);

  // Two whole sentences rather than a number spliced into one: where the count sits in the
  // sentence is not the same in every language.
  return max === undefined ? t('errors.hostLimitUnknown') : t('errors.hostLimit', { max });
};

/**
 * A 429, phrased as a wait rather than a code.
 *
 * Windows are fixed rather than sliding, so a precise countdown would be a lie — round up,
 * because telling somebody to wait a minute when it is really two is worse than the reverse. The
 * plural is a vue-i18n one rather than an inline ternary: "1 minuut / 2 minute" is not a rule
 * English can pick for Afrikaans.
 *
 * Lives here rather than in a composable because message selection is what this file does, and
 * because sign-in was about to have a second identical copy in the contact form.
 */
export const rateLimitMessage = (
  error: Pick<ApiError, 'code' | 'message' | 'retryAfterSeconds'>,
  /**
   * The sentence to put the number in. `JoinView` and `CreateGameView` each re-derived the minutes
   * by hand — the same rounding, the same undefined branch — for the sole reason that they wanted
   * to name what was limited: "too many joins", not "too many requests". The rounding rule is the
   * shared part; the sentence is not, so the sentence is the parameter.
   */
  waitKey = 'errors.waitMinutes',
): string => {
  const seconds = error.retryAfterSeconds;
  // Reached before the key, deliberately: with no number there is nothing to pluralise, and a
  // plural message rendered without its count reads as a template that failed.
  if (seconds === undefined) return userMessage(error.code, error.message);

  return t(waitKey, Math.max(1, Math.ceil(seconds / 60)));
};
